import SectionHeader from "../../../../util/component/SectionHeader";
import {Button, Grid, MenuItem, Paper, TextField, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import '../../style/SalesMarketing.css';
import * as Yup from 'yup';
import {DatePicker} from "@mui/x-date-pickers";
import {useProductCodeActions} from "../../service/useProductCodeActions";
import {useRecoilState} from "recoil";
import {
    BILLING_MODES,
    CATEGORY_LIST,
    DEPOSIT_CODES_LIST,
    GUARANTEE_BILLING_MODE_REL,
    GUARANTEE_PAYMENT_METHOD_REL,
    GUARANTEE_PAYMENT_TIMING_REL,
    GUARANTEE_SCHEMES,
    INVOICE_RECIPIENTS,
    PAYMENT_DUE_DATES,
    PAYMENT_METHDOS,
    PAYMENT_TIMINGS,
    PRODUCT_STATUS_LIST,
    SEGMENT_LIST,
    SEGMENTS_NO_GUARANTEE
} from "../../service/SalesMarketingRecoil";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import ProductCodeUpdateModal from "./modals/ProductCodeUpdateModal";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {boolToString, floatToIntCurrency, intToFloatCurrency, stringToBool} from "../../component/Utils";
import TaxConfigurationAccordion from "../../component/TaxConfiguration/TaxConfigurationAccordion";
import { GuaranteePaymentTimingCodes, PaymentMethodsValues, PaymentTimingsValues } from "./ProductCodeCreatePage";
import { UserLegalEntitiesOrDealersInputGroup } from "../../component/UserLegalEntitiesOrDealersInputGroup/UserLegalEntitiesOrDealersInputGroup";


interface ProductCodeDetail {
    id?: number,
    tenantId: string,
    dealerId: string,
    productCode?: string,
    statusCode?: string,
    name?: string,
    segmentCode?: string,
    preAuthCode?: string,
    categoryCode?: string,
    description?: string,
    startDate?: Date,
    endDate?: Date,
    paymentMethod?: string,
    paymentDueDate?: string,
    billingMode?: string,
    guarantee?: string,
    firstRecipient?: string,
    subsequentRecipient?: string,
    paymentTiming?: string,
    fastLine?: string,
    tax_configuration?: {
        railwayStationTax?: string,
        roadTax?: string,
        airportTax?: string,
        airportRailwayStationTax?: string,
        roadTaxDailyValue?: string,
        reservedPricing?: string
    }
}

const ProductCodeUpdatePage = () => {
    const theme = useTheme();
    const {t} = useTranslation(['salesmarketing', 'translation']);
    const productCodeActions = useProductCodeActions();
    const [segments] = useRecoilState(SEGMENT_LIST);
    const [categoriesMap] = useRecoilState(CATEGORY_LIST);
    const [depositList] = useRecoilState(DEPOSIT_CODES_LIST);
    const [productStatusList] = useRecoilState(PRODUCT_STATUS_LIST);
    const [paymentMethods] = useRecoilState(PAYMENT_METHDOS);
    const [guaranteeSchemes] = useRecoilState(GUARANTEE_SCHEMES);
    const [paymentDueDates] = useRecoilState(PAYMENT_DUE_DATES);
    const [paymentTimings] = useRecoilState(PAYMENT_TIMINGS);
    const [invoiceRecipients] = useRecoilState(INVOICE_RECIPIENTS);
    const [billingModes] = useRecoilState(BILLING_MODES);
    const [segmentsNoGuarantee] = useRecoilState(SEGMENTS_NO_GUARANTEE);
    const [guaranteePaymentMethodRel] = useRecoilState(GUARANTEE_PAYMENT_METHOD_REL);
    const [guaranteePaymentTimingRel] = useRecoilState(GUARANTEE_PAYMENT_TIMING_REL);
    const [guaranteeBillingModeRel] = useRecoilState(GUARANTEE_BILLING_MODE_REL);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [productCode, setProductCode] = useState<ProductCodeDetail>();
    const [modalOpen, setModalOpen] = useState(false);

    const productCodeParams = searchParams.get("productCode") as string

    const fetchProductCode = async () => {
        setProductCode(await productCodeActions.findProductCode(productCodeParams));
    }

    const {globalData} = useContext(GlobalContext);

    useEffect(() => {
        productCodeActions.listSegment();
        // productCodeActions.listCategory(); // Categories will be loaded dynamically based on product's tenant
        productCodeActions.listProductStatus();
        productCodeActions.listPaymentTimings();
        productCodeActions.listInvoiceRecipients();
        fetchProductCode();
    }, [])

    useEffect(() => {
        if (productCode?.tenantId) {
            productCodeActions.listCategory(productCode.tenantId);
            productCodeActions.listBillingModes(productCode.tenantId);
        }
        reset(retrieveInitialValues())
    }, [productCode])

    const retrieveInitialValues = () => {
        return {
            tenantId: productCode?.tenantId ? productCode.tenantId : "",
            dealerId: productCode?.dealerId ? productCode.dealerId : "",
            status: productCode?.statusCode ? productCode.statusCode : "",
            productCode: productCode?.productCode ? productCode.productCode : "",
            name: productCode?.name ? productCode.name : "",
            segment: productCode?.segmentCode ? productCode.segmentCode : "",
            preAuthCode: productCode?.preAuthCode ? productCode?.preAuthCode : "",
            category: productCode?.categoryCode ? productCode.categoryCode : "",
            description: productCode?.description ? productCode.description : "",
            paymentMethod: productCode?.paymentMethod ? productCode.paymentMethod : PaymentMethodsValues.CREDIT_CARD,
            paymentDueDate: productCode?.paymentDueDate ? productCode.paymentDueDate : "",
            guarantee: productCode?.guarantee ? productCode.guarantee : GuaranteePaymentTimingCodes.NO_CREDIT,
            billingMode: productCode?.billingMode ? productCode.billingMode : '',
            startDate: productCode?.startDate ? productCode.startDate : null,
            endDate: productCode?.endDate ? productCode.endDate : null,
            firstRecipient: productCode?.firstRecipient ? productCode.firstRecipient : '',
            subsequentRecipient: productCode?.subsequentRecipient ? productCode.subsequentRecipient : '',
            paymentTiming: productCode?.paymentTiming ? productCode.paymentTiming : PaymentTimingsValues.PREPAID,
            fastLine: productCode?.fastLine ? stringToBool(productCode.fastLine) : false,
            tax_configuration: {
                railwayStationTax: productCode?.tax_configuration?.railwayStationTax ? productCode?.tax_configuration?.railwayStationTax : 'S',
                roadTax: productCode?.tax_configuration?.roadTax ? productCode?.tax_configuration?.roadTax : 'S',
                airportTax: productCode?.tax_configuration?.airportTax ? productCode?.tax_configuration?.airportTax : 'S',
                airportRailwayStationTax:productCode?.tax_configuration?.airportRailwayStationTax ? productCode?.tax_configuration?.airportRailwayStationTax : '',
                roadTaxDailyValue: productCode?.tax_configuration?.roadTaxDailyValue ? intToFloatCurrency(productCode.tax_configuration?.roadTaxDailyValue) : '',
                reservedPricing: productCode?.tax_configuration?.reservedPricing ? productCode?.tax_configuration?.reservedPricing : ''
            }

        }
    };

    const validationSchema = Yup.object({
        status: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        productCode: Yup.string().nullable(),
        name: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        segment: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        preAuthCode: Yup.string().nullable(),
        category: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        description: Yup.string().nullable(),
        paymentMethod: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        paymentDueDate: Yup.string().nullable(),
        guarantee: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        billingMode: Yup.string().nullable(),
        startDate: Yup.date().nullable().test("testStartDate", "*" + t('salesmarketing:product.createForm.datesCheck'), (value, context) => {
            return !value || !context.parent.endDate || (value <= context.parent.endDate);
        }).typeError(t('product.createForm.invalidDate')),
        endDate: Yup.date().nullable().test("testEndDate", "*" + t('salesmarketing:product.createForm.datesCheck'), (value, context) => {
            return !value || !context.parent.startDate || (value >= context.parent.startDate);
        }).typeError(t('product.createForm.invalidDate')),
        firstRecipient: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        subsequentRecipient: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        paymentTiming: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        tax_configuration :Yup.object({
            airportRailwayTaxPercentage: Yup.number().min(0).max(100)
        })
    });

    const onSubmit = async (data: any) => {
        let payload = {...data, fastLine: boolToString(data.fastLine),  tax_configuration : {
                ...data.tax_configuration, roadTaxDailyValue : data.tax_configuration.roadTaxDailyValue ? floatToIntCurrency(data.tax_configuration.roadTaxDailyValue) : ''
            }};
        if (data.segment !== 'TOP')
            delete payload.fastLine;
        let result = await productCodeActions.updateProductCode(payload, productCode?.id);
        setModalOpen(result && result?.status === 'OK');
    };

    const methods = useForm({
        defaultValues: retrieveInitialValues(),
        resolver: yupResolver(validationSchema)
    });

    const {handleSubmit, control, setValue, watch, reset} = methods;

    useEffect(() => {
        let tenantId = watch('tenantId');
        let dealerId = watch('dealerId');
        if (tenantId && tenantId !== productCode?.tenantId) {
            productCodeActions.listCategory(tenantId);
            productCodeActions.listBillingModes(tenantId);
            setValue('category', '');
        }
        if(tenantId && dealerId){
            productCodeActions.listPaymentDueDates(tenantId, dealerId);
            productCodeActions.listGuaranteeSchemes(tenantId, dealerId);
            productCodeActions.listPaymentMethods(tenantId, dealerId);
            productCodeActions.getPreAuthorizationList(tenantId, dealerId);
        }
    }, [watch('tenantId'), watch('dealerId'), productCode]);


    return (
        <>
            <SectionHeader title={t('salesmarketing:product.updateHeader.title')}
                           subtitle={t('salesmarketing:product.updateHeader.subTitle')}/>
            <Paper elevation={3} sx={{padding: "16px", margin: "18px"}}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <Grid container rowSpacing={4}>
                            <Grid container item spacing={1}>
                            <UserLegalEntitiesOrDealersInputGroup
                                control={control}
                                disabled={!!searchParams.get("productCode")}
                            />
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"status"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.statusField')}
                                                select
                                                disabled={productCode?.statusCode === 'D'}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                {productStatusList?.map((s) => {
                                                    return (s.statusCode === 'D' && productCode?.statusCode !== 'D') ? null :
                                                        <MenuItem key={s.statusCode} value={s.statusCode}>
                                                            {s.statusName}
                                                        </MenuItem>
                                                })}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"productCode"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.productCodeField')}
                                                disabled
                                                placeholder={t('salesmarketing:product.createForm.productCodePlaceholder')}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={6}/>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"name"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.nameField')}
                                                placeholder={t('salesmarketing:product.createForm.namePlaceholder')}
                                                required
                                                inputProps={{maxLength: 30}}
                                                disabled={productCode?.statusCode === 'R'}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"segment"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.segmentField')}
                                                select
                                                required
                                                disabled={productCode?.statusCode === 'R' || watch('segment') === 'RET'}
                                                value={field.value}
                                                onChange={(e) => {
                                                    field.onChange(e)
                                                    setValue('category', '')
                                                    if (segmentsNoGuarantee.includes(e.target.value)) {
                                                        setValue('guarantee', 'NO_CREDIT');
                                                        setValue('paymentMethod', '');
                                                        setValue('paymentTiming', '');
                                                        setValue('billingMode', '');
                                                    }
                                                }}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {segments?.map((s) => (
                                                    <MenuItem key={s.segmentCode} value={s.segmentCode}>
                                                        {s.segmentName}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"category"}
                                        render={({field, fieldState}) => {
                                            const selectedTenantId = watch('tenantId');
                                            const availableCategories = categoriesMap[selectedTenantId] || [];
                                            const isCategoryDisabled = productCode?.statusCode === 'R' || !selectedTenantId || availableCategories.length === 0;

                                            return (
                                                <TextField
                                                    fullWidth
                                                    label={t('salesmarketing:product.createForm.categoryField')}
                                                    select
                                                    required
                                                    disabled={isCategoryDisabled}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                    <MenuItem key={""} value={""}/>
                                                    {availableCategories.filter(c => c.segmentCode === watch("segment"))?.map((c) => (
                                                        <MenuItem key={c.categoryCode} value={c.categoryCode}>
                                                            {c.categoryLabel}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            );
                                        }}/>
                                </Grid>
                                {/*{(watch('segment') === 'TOP') &&
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"fastLine"}
                                        render={({field, fieldState}) => (
                                            <FormControlLabel
                                                sx={{mt: 3}}
                                                label={t('salesmarketing:product.createForm.fastLine')}
                                                control={<Checkbox
                                                    color={"accent"}
                                                    sx={{color: theme.palette.accent.main}}
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                />}
                                            />
                                        )}/>
                                </Grid>
                            }*/}
                                <Grid item xs={6}>
                                    <Controller
                                        control={control}
                                        name={"description"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.descriptionField')}
                                                placeholder={t('salesmarketing:product.createForm.descriptionPlaceholder')}
                                                inputProps={{maxLength: 120}}
                                                disabled={productCode?.statusCode === 'R'}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"startDate"}
                                        render={({field, fieldState}) => (
                                            <DatePicker
                                                label={t('salesmarketing:product.createForm.startDateField')}
                                                disabled={productCode?.statusCode === 'R'}
                                                value={field.value}
                                                onChange={field.onChange}
                                                renderInput={(params) =>
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}/>
                                                }
                                                inputFormat={"L"}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"endDate"}
                                        render={({field, fieldState}) => (
                                            <DatePicker
                                                label={t('salesmarketing:product.createForm.endDateField')}
                                                disabled={productCode?.statusCode === 'R'}
                                                value={field.value}
                                                onChange={field.onChange}
                                                renderInput={(params) =>
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}/>
                                                }
                                                inputFormat={"L"}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"preAuthCode"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                name="preAuthCode"
                                                label={t('salesmarketing:product.deposit')}
                                                select
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {depositList?.map((a) => (
                                                    <MenuItem key={a.id} value={a.id}>
                                                        {a.description}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"guarantee"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.guaranteeField')}
                                                select
                                                required
                                                disabled={productCode?.statusCode === 'R' || segmentsNoGuarantee.includes(watch("segment"))}
                                                value={field.value}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                    setValue('paymentMethod', '');
                                                    if(e.target.value === GuaranteePaymentTimingCodes.STANDARD_CREDIT || e.target.value === GuaranteePaymentTimingCodes.FULL_CREDIT){
                                                        setValue('paymentTiming',PaymentTimingsValues.DROPOFF);
                                                        setValue('paymentMethod',PaymentMethodsValues.BANK_TRANSFER)
                                                    }
                                                    if (e.target.value === GuaranteePaymentTimingCodes.NO_CREDIT) {
                                                        setValue('paymentTiming',PaymentTimingsValues.PREPAID)
                                                        setValue('paymentMethod',PaymentMethodsValues.CREDIT_CARD);
                                                    }
                                                    let billingModesCodes = guaranteeBillingModeRel?.find(b => b.code === watch("guarantee"))?.billingModeCodes;
                                                    if (billingModesCodes && billingModesCodes?.length === 1) {
                                                        setValue('billingMode', billingModesCodes[0]);
                                                    } else {
                                                        setValue('billingMode', '');
                                                    }
                                                }}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                
                                                {guaranteeSchemes?.map((s) => (
                                                    <MenuItem key={s.label} value={s.value}>
                                                        {s.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"paymentMethod"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.paymentMethodField')}
                                                select
                                                required
                                                disabled={productCode?.statusCode === 'R' || !watch("guarantee")}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                {guaranteePaymentMethodRel?.find(g => g.code === watch("guarantee"))?.paymentMethodCodes.map((method) => {
                                                    let pm = paymentMethods.find(pm => pm.value === method);
                                                    return pm ? <MenuItem key={pm.label} value={pm.value}>
                                                        {pm.label}
                                                    </MenuItem> : null;
                                                })}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"paymentDueDate"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.paymentDueDateField')}
                                                select
                                                disabled={productCode?.statusCode === 'R'}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {paymentDueDates?.map((s) => (
                                                    <MenuItem key={s.label} value={s.value}>
                                                        {s.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"billingMode"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.billingModeField')}
                                                select
                                                disabled={productCode?.statusCode === 'R' || !watch('guarantee') || guaranteeBillingModeRel?.find(b => b.code === watch("guarantee"))?.billingModeCodes?.length === 1}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {guaranteeBillingModeRel?.find(b => b.code === watch("guarantee"))?.billingModeCodes.map((mode) => {
                                                    let bm = (billingModes[watch('tenantId')] || []).find(bm => bm.value === mode);
                                                    return bm ? <MenuItem key={bm.label} value={bm.value}>
                                                        {bm.label}
                                                    </MenuItem> : null;
                                                })}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"firstRecipient"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.firstRecipientField')}
                                                select
                                                required
                                                disabled={productCode?.statusCode === 'R'}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {invoiceRecipients?.map((r) => (
                                                    <MenuItem key={r.id} value={r.id}>
                                                        {r.recipientType}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"subsequentRecipient"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.subsequentRecipientField')}
                                                select
                                                required
                                                disabled={productCode?.statusCode === 'R'}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {invoiceRecipients?.map((r) => (
                                                    <MenuItem key={r.id} value={r.id}>
                                                        {r.recipientType}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"paymentTiming"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.paymentTimingField')}
                                                select
                                                required
                                                disabled={productCode?.statusCode === 'R' || !watch('guarantee') || guaranteePaymentTimingRel?.find(b => b.code === watch("guarantee"))?.paymentTimingCodes?.length === 0 || guaranteePaymentTimingRel?.find(b => b.code === watch("guarantee"))?.paymentTimingCodes?.length === 1}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                {guaranteePaymentTimingRel?.find(b => b.code === watch("guarantee"))?.paymentTimingCodes?.map((timing) => {
                                                    let pt = paymentTimings.find(pt => pt.id === timing);
                                                    return pt ? <MenuItem key={pt.id} value={pt.id}>
                                                        {pt.value}
                                                    </MenuItem> : null;
                                                })}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={12} sx={{marginTop: '50px'}}>
                                    <TaxConfigurationAccordion prefix={'tax_configuration'}/>
                                </Grid>
                            </Grid>
                            <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                                <Grid container item xs={12} rowSpacing={1} columnSpacing={3}
                                      justifyContent={"flex-end"}>
                                    <Grid item>
                                        <Button fullWidth size={"small"} variant="outlined" onClick={() => {
                                            navigate("/salesmarketing/productcodelist")
                                        }}>
                                            {t('salesmarketing:product.buttons.cancel')}
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button fullWidth size={"small"} color={"accent"}
                                                type="submit">{t('salesmarketing:product.buttons.confirm')}</Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </form>
                </FormProvider>
            </Paper>
            <ProductCodeUpdateModal open={modalOpen} setOpen={setModalOpen}/>
        </>
    );
}

export default ProductCodeUpdatePage;